import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import PKCE from "js-pkce";
import config from "@src/config";
import { AcSaveState, AcClearState, KEYS, AcGetState } from "@opp/ui-kit";
import moment from "moment";
import { LoadingState } from "../types";
import { AuthAPI } from "@src/api/auth.api";
import ITokenResponse from "js-pkce/dist/ITokenResponse";

const mappedStorage = {
  [KEYS.EXPIRES_AT]: AcGetState(KEYS.EXPIRES_AT),
  [KEYS.TOKEN_TYPE]: AcGetState(KEYS.TOKEN_TYPE),
  [KEYS.ACCESS_TOKEN]: AcGetState(KEYS.ACCESS_TOKEN),
};

type AuthState = {
  loading: LoadingState;
  isAuthorized: boolean;
  currentPermissions: any[];
  currentRoles: any[];
} & Record<keyof typeof mappedStorage, string | number>;

const initialState: AuthState = {
  loading: "idle",
  isAuthorized: !!AcGetState(KEYS.ACCESS_TOKEN),
  currentPermissions: [],
  currentRoles: [],
  ...mappedStorage,
};

const pkce = new PKCE({
  client_id: config.pkce.client_id,
  redirect_uri: config.pkce.redirect_uri,
  authorization_endpoint: config.pkce.authorization_endpoint,
  token_endpoint: config.pkce.token_endpoint,
  requested_scopes: "",
});


export const handleLogout = createAsyncThunk("auth/logout", async () => {
  const response = await AuthAPI.logout();
  return response.data;
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    handleAppAuthentication(state, action: PayloadAction<ITokenResponse>) {
      const { payload } = action;

      let account = payload;

      const { access_token, expires_in, token_type } = account;

      const expires_at = moment().add(expires_in, "seconds").format("X");

      state.expires_at = expires_at;
      AcSaveState(KEYS.EXPIRES_AT, expires_at);
      if (access_token) {
        state.access_token = access_token;
        AcSaveState(KEYS.ACCESS_TOKEN, access_token);
      }
      if (token_type) {
        state.token_type = token_type;
        AcSaveState(KEYS.TOKEN_TYPE, token_type);
      }
      state.isAuthorized = true;
    },
    handleRefreshToken(
      state,
      action: PayloadAction<{ access_token: string; expires_in: number }>
    ) {
      const {
        payload: { access_token, expires_in },
      } = action;

      const expires_at = moment().add(expires_in, "seconds").format("X");

      state.expires_at = expires_at;
      AcSaveState(KEYS.EXPIRES_AT, expires_at);
      if (access_token) {
        state.access_token = access_token;
        AcSaveState(KEYS.ACCESS_TOKEN, access_token);
      }
      state.isAuthorized = true;
    },
    handlePKCE() {
      const uri = pkce.authorizeUrl();
      localStorage.setItem("redirectUriFE", window.location.href);
      window.location.replace(uri);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(handleLogout.fulfilled, () => {
      const uri = pkce.authorizeUrl();
      window.location.replace(uri);
      AcSaveState(KEYS.ACCOUNT, null);
      AcSaveState(KEYS.ACCESS_TOKEN, null);
      AcSaveState(KEYS.TOKEN_TYPE, null);
      AcSaveState(KEYS.EXPIRES_IN, null);
      AcSaveState(KEYS.EXPIRES_AT, null);
      AcSaveState(KEYS.PROFILE, null);
      AcClearState();
      return { ...initialState, isAuthorized: false, access_token: null };
    });
    builder.addCase(handleLogout.rejected, () => {
      const uri = pkce.authorizeUrl();
      window.location.replace(uri);
      AcSaveState(KEYS.ACCOUNT, null);
      AcSaveState(KEYS.ACCESS_TOKEN, null);
      AcSaveState(KEYS.TOKEN_TYPE, null);
      AcSaveState(KEYS.EXPIRES_IN, null);
      AcSaveState(KEYS.EXPIRES_AT, null);
      AcSaveState(KEYS.PROFILE, null);
      AcClearState();
      return { ...initialState, isAuthorized: false, access_token: null };
    });
  },
});

export const { handleAppAuthentication, handleRefreshToken, handlePKCE } =
  authSlice.actions;

export default authSlice.reducer;
